import { t } from '@/plugins/i18n';

/**
 * Routes
 */

const ROUTE_PARAM_IDS = {
  alerts: 'alerts',
  rules: 'rules',
  events: 'events',
  summary: 'summary',
  vehicleList: 'vehicleList',
};

const ROUTE_NAMES = {
  alerts: 'Alerts',
  rules: 'Rules',
  events: 'Events',
  summary: 'Summary',
  vehicleList: 'Vehicle list',
  singleVehicleList: 'Single vehicle list',
  vehicleUploadViaCSV: 'Upload list via CSV',
  addVehicle: 'Add vehicle',
  addVehicleFromSearch: 'Add vehicle search',
  editVehicle: 'Edit vehicle',
  addRule: 'Add VSP rule',
  editRule: 'Edit VSP rule',
  vspMap: 'Vehicle search',
};

const ROUTES = {
  // Vehicle list
  singleVehicleListPage: (id: string) => `/vsp/vehicleList/${id}`,
  vehicleListAddPage: () => '/vsp/vehicleList/add',
  vehicleListsPage: () => '/vsp/vehicleList',
  vehicleAddPage: (id: string) => `/vsp/vehicleList/${id}/add-vehicle`,
  csvVehicleUpload: (id: string) => `/vsp/vehicleList/${id}/csv-upload`,
  vehicleEditPage: (listID: string, recordID: string) => `/vsp/vehicleList/${listID}/${recordID}/edit-vehicle`,
  addVehiclePage: () => `/vsp/vehicleList/add-vehicle`,

  // Rules
  editRulePage: (id: string) => `/vsp/rules/${id}/edit-rule`,
  addRulePage: () => '/vsp/rules/add-rule',
  rulesPage: () => '/vsp/rules',

  // Events
  searchPage: () => '/vsp/events',
  mapPage: () => `/vsp/events/maps`,
};

/**
 * General
 */

const TABLE_LOADING_ITEMS_LENGTH = 4;
const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_PAGE = 1;

const LPR_EVENT_DATA_TYPES = {
  accessType: 'een.lprAccessType.v1',
  croppedFrameImageUrl: 'een.croppedFrameImageUrl.v1',
  display: 'een.display.v1',
  fullFrameImageUrl: 'een.fullFrameImageUrl.v1',
  lprDetection: 'een.lprDetection.v1',
  objectDetection: 'een.objectDetection.v1',
  userData: 'een.userData.v1',
  vehicleAttributes: 'een.vehicleAttributes.v1',
  vspInsightsSummary: 'een.vspInsightsSummary.v1',
  vehicleListInfo: 'een.vehicleListInfo.v1',
};

const FLEET_CODE_EVENT_TYPES = {
  lprDetection: { name: 'License plate', type: 'een.lprDetection.v1' },
  fullFrameImageUrl: { name: 'Full Image', type: 'een.fullFrameImageUrl.v1' },
  croppedFrameImageUrl: { name: 'Cropped Image', type: 'een.croppedFrameImageUrl.v1' },
  dotNumberRecognition: { name: 'DOT number', type: 'een.dotNumberRecognition.v1' },
  truckNumberRecognition: { name: 'Truck number', type: 'een.truckNumberRecognition.v1' },
  trailerNumberRecognition: { name: 'Trailer number', type: 'een.trailerNumberRecognition.v1' },
  recognizedText: { name: 'Recognized text', type: 'een.recognizedText.v1' },
  objectDetection: { name: 'Object detection', type: 'een.objectDetection.v1' },
};

const UNKNOWN_EVENT_COUNT = -1;

const VEHICLE_RECORD_CSV_HEADER_INDEX_OFFSET = 2;

const TOTAL_EVENTS_SIZE = 5000;

const DEFAULT_TABLE_HEADER_SKELETON = [
  { text: '-', skeletonWidth: '275px' },
  { text: '-', skeletonWidth: '275px' },
  { text: '-', skeletonWidth: '200px' },
  { text: '-', skeletonWidth: '200px' },
  { text: '-', skeletonWidth: '200px' },
  { skeletonWidth: '0px' },
  { skeletonWidth: '0px' },
];

const ALPHA_NUMERIC_HYPHEN_REGEX = /[^A-Za-z0-9*-]/;

const ALPHA_NUMERIC_REGEX = /[A-Za-z0-9]/;
const SPECIAL_CHAR_REGEX = /[$&+,:;=?@#|'<>.^()%!_ ]/g;

const V3_URL_SUBSTRING = '/api/v3.0/';

enum VSP_RULES_FILTERTYPE {
  plate = 'plate',
  vehicleList = 'vehicleList',
  anyPlateExcept = 'anyPlateExcept',
}

const DEFAULT_DATE_TIME_PRESET = {
  last24Hours: 'Last 24 hours',
  last7Days: 'Last 7 days',
  last30Days: 'Last 30 days',
  today: 'Today',
  yesterday: 'Yesterday',
};

const DATE_RANGE_OPTIONS = [
  { name: 'Last 24 hours', value: 24, key: 'h' },
  { name: 'Last 7 days', value: 7, key: 'd' },
  { name: 'Last 30 days', value: 30, key: 'd' },
  { name: 'Today', value: null, key: null },
];

const CUSTOM_DATE_RANGE_PRESET = 'custom';

const FLEET_IDENTIFIERS = {
  DOT: 'dot',
  TRAILER: 'trailer',
  TRUCK: 'truck',
};

const FETCH_EVENTS_TIMEOUT = 1000;

const FILTER_TYPES = {
  SITE: 'site',
  CAMERA: 'camera',
  DIRECTION: 'direction',
};

const DATE_TIME_PRESET_VALUES = [
  {
    key: 'h',
    value: 24,
    name: DEFAULT_DATE_TIME_PRESET.last24Hours,
  },
  {
    key: 'd',
    value: 7,
    name: DEFAULT_DATE_TIME_PRESET.last7Days,
  },
  {
    key: 'd',
    value: 30,
    name: DEFAULT_DATE_TIME_PRESET.last30Days,
  },
  {
    key: '',
    value: '',
    name: DEFAULT_DATE_TIME_PRESET.today,
  },
];

const FIRST_INDEX = 0;

const MAX_INSIGHTS = 4;

const VSP_INSIGHTS_TYPE = {
  lastSeenCamera: 'lastSeenCamera',
  lastSeenTimestamp: 'lastSeenTimestamp',
  count: 'count',
  countLast24h: 'countLast24h',
  countLast7d: 'countLast7d',
  new: 'new',
  unregistered: 'unregistered',
};

const DURATION_TYPES = {
  lastTwentyFourHours: 'lastTwentyFourHours',
  thisWeek: 'thisWeek',
  lastSevenDays: 'lastSevenDays',
  lastThirtyDays: 'lastThirtyDays',
};

const EVENT_SIDEBAR_MEDIA_ITEMS = {
  licensePlate: 'licensePlate',
  eventImage: 'eventImage',
  eventVideo: 'eventVideo',
};

const VEHICLE_ACTIVITY_EVENT_SKELETON_COUNT = 5;
const VEHILCE_ACTIVITY_EVENT_DETAILS_SKELETON_COUNT = 2;

const USER_DATA_KEY_VALUE_COUNT = 2;

export {
  ALPHA_NUMERIC_HYPHEN_REGEX,
  ALPHA_NUMERIC_REGEX,
  DEFAULT_PAGE,
  CUSTOM_DATE_RANGE_PRESET,
  DATE_RANGE_OPTIONS,
  DEFAULT_DATE_TIME_PRESET,
  DEFAULT_PAGE_SIZE,
  DATE_TIME_PRESET_VALUES,
  DEFAULT_TABLE_HEADER_SKELETON,
  DURATION_TYPES,
  EVENT_SIDEBAR_MEDIA_ITEMS,
  FILTER_TYPES,
  FIRST_INDEX,
  FLEET_CODE_EVENT_TYPES,
  FLEET_IDENTIFIERS,
  LPR_EVENT_DATA_TYPES,
  MAX_INSIGHTS,
  ROUTE_NAMES,
  ROUTE_PARAM_IDS,
  ROUTES,
  SPECIAL_CHAR_REGEX,
  TABLE_LOADING_ITEMS_LENGTH,
  FETCH_EVENTS_TIMEOUT,
  TOTAL_EVENTS_SIZE,
  UNKNOWN_EVENT_COUNT,
  USER_DATA_KEY_VALUE_COUNT,
  VEHILCE_ACTIVITY_EVENT_DETAILS_SKELETON_COUNT,
  VEHICLE_ACTIVITY_EVENT_SKELETON_COUNT,
  VEHICLE_RECORD_CSV_HEADER_INDEX_OFFSET,
  VSP_INSIGHTS_TYPE,
  VSP_RULES_FILTERTYPE,
  V3_URL_SUBSTRING,
};
